/* global axios */
import ApiClient from './ApiClient';

class TicketsAPI extends ApiClient {
  constructor() {
    super('tickets', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(this.url, { params });
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data) {
    return axios.post(this.url, { ticket: data });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, { ticket: data });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  bulkActions(data) {
    return axios.post(`${this.url}/bulk_actions`, data);
  }

  // Minimal, read-only list of a contact's tickets for the Resolve-and-Append modal.
  // Available to every agent (unlike the permission-gated index).
  appendable(contactId) {
    return axios.get(`${this.url}/appendable`, {
      params: { contact_id: contactId },
    });
  }

  getNotes(ticketId) {
    return axios.get(`${this.url}/${ticketId}/ticket_notes`);
  }

  createNote(ticketId, data) {
    const { files, ...rest } = data;
    if (files && files.length) {
      const form = new FormData();
      Object.entries(rest).forEach(([k, v]) => {
        if (v != null && v !== '') form.append(`ticket_note[${k}]`, v);
      });
      files.forEach(f => form.append('ticket_note[files][]', f));
      return axios.post(`${this.url}/${ticketId}/ticket_notes`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.post(`${this.url}/${ticketId}/ticket_notes`, {
      ticket_note: rest,
    });
  }

  updateNote(ticketId, noteId, data) {
    const { newFiles, removeAttachments, ...rest } = data;
    const hasFileChanges =
      (newFiles && newFiles.length) || (removeAttachments && removeAttachments.length);
    if (hasFileChanges) {
      const form = new FormData();
      Object.entries(rest).forEach(([k, v]) => {
        if (v != null && v !== '') form.append(`ticket_note[${k}]`, v);
      });
      if (newFiles) newFiles.forEach(f => form.append('ticket_note[files][]', f));
      if (removeAttachments)
        removeAttachments.forEach(id => form.append('remove_attachments[]', id));
      return axios.patch(`${this.url}/${ticketId}/ticket_notes/${noteId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.patch(`${this.url}/${ticketId}/ticket_notes/${noteId}`, {
      ticket_note: rest,
    });
  }

  deleteNote(ticketId, noteId) {
    return axios.delete(`${this.url}/${ticketId}/ticket_notes/${noteId}`);
  }

  getActivities(ticketId) {
    return axios.get(`${this.url}/${ticketId}/ticket_activities`);
  }

  forward(ticketId, data) {
    if (data instanceof FormData) {
      return axios.post(`${this.url}/${ticketId}/forward`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.post(`${this.url}/${ticketId}/forward`, data);
  }

  uploadDescriptionFiles(ticketId, files) {
    const form = new FormData();
    files.forEach(f => form.append('files[]', f));
    return axios.post(`${this.url}/${ticketId}/upload_description_files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  removeDescriptionFile(ticketId, fileId) {
    return axios.delete(`${this.url}/${ticketId}/remove_description_file`, {
      params: { file_id: fileId },
    });
  }

  syncThreads(ticketId) {
    return axios.post(`${this.url}/${ticketId}/sync_threads`);
  }

  replyThread(ticketId, data) {
    if (data instanceof FormData) {
      return axios.post(`${this.url}/${ticketId}/reply_thread`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.post(`${this.url}/${ticketId}/reply_thread`, data);
  }
}

export default new TicketsAPI();
